import { adminFetch } from "./base";

export type LaunchPhase =
  | "technique"
  | "catalogue"
  | "agents"
  | "marketing"
  | "produits";
export type LaunchPriority = "critique" | "best_practice";
export type LaunchStatus = "todo" | "in_progress" | "done";

export interface LaunchAssignee {
  readonly id: string;
  readonly full_name: string;
  readonly email: string;
}

export interface LaunchTask {
  readonly id: string;
  readonly phase: LaunchPhase;
  readonly title: string;
  readonly description: string | null;
  readonly priority: LaunchPriority;
  readonly status: LaunchStatus;
  readonly assigned_to: string | null;
  readonly assignee: LaunchAssignee | null;
  readonly completed_at: string | null;
  readonly completed_by: string | null;
  readonly sort_order: number;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface LaunchTaskListParams {
  readonly phase?: LaunchPhase;
  readonly priority?: LaunchPriority;
  readonly status?: LaunchStatus;
  readonly assigned_to_me?: boolean;
}

export interface LaunchTaskListResponse {
  readonly tasks: readonly LaunchTask[];
}

export interface UpdateLaunchTaskInput {
  readonly status?: LaunchStatus;
  readonly assigned_to?: string | null;
  readonly title?: string;
  readonly description?: string | null;
}

export interface CreateLaunchTaskInput {
  readonly phase: LaunchPhase;
  readonly title: string;
  readonly description?: string;
  readonly priority: LaunchPriority;
  readonly assigned_to?: string;
}

function toQueryString(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v != null && v !== "",
  );
  if (entries.length === 0) return "";
  return (
    "?" +
    new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()
  );
}

export const launchApi = {
  listTasks(params: LaunchTaskListParams): Promise<LaunchTaskListResponse> {
    const qs = toQueryString({
      phase: params.phase,
      priority: params.priority,
      status: params.status,
      assigned_to_me: params.assigned_to_me ? "true" : undefined,
    });
    return adminFetch<LaunchTaskListResponse>(`/api/admin/launch${qs}`);
  },

  updateTask(
    id: string,
    input: UpdateLaunchTaskInput,
  ): Promise<{ readonly task: LaunchTask }> {
    return adminFetch<{ readonly task: LaunchTask }>(
      `/api/admin/launch/${encodeURIComponent(id)}`,
      {
        method: "PATCH",
        body: JSON.stringify(input),
      },
    );
  },

  createTask(
    input: CreateLaunchTaskInput,
  ): Promise<{ readonly task: LaunchTask }> {
    return adminFetch<{ readonly task: LaunchTask }>("/api/admin/launch", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
} as const;
